import { useState } from "react";
import { Bot, CheckCircle2, Folder } from "lucide-react";
import { Button } from "~/components/ui/button";
import { cn } from "~/lib/utils";
import { ProjectAgentsDialog } from "./ProjectAgentsDialog";
import { ProjectDatasetsDialog } from "./ProjectDatasetsDialog";
import { useProjectContext } from "./useProjectContext";

interface ProjectSetupWizardProps {
  onContinue: () => void;
}

export const ProjectSetupWizard = ({ onContinue }: ProjectSetupWizardProps) => {
  const { projectId, attachedAgents, attachedDatasets, isOwner } = useProjectContext();
  const [agentsDialogOpen, setAgentsDialogOpen] = useState(false);
  const [datasetsDialogOpen, setDatasetsDialogOpen] = useState(false);

  const hasAgents = (attachedAgents?.length ?? 0) > 0;
  const hasDatasets = (attachedDatasets?.length ?? 0) > 0;

  // A non-owner can't add agents or datasets, so the setup actions are useless
  // to them — show a hand-off message instead of dead buttons.
  if (!isOwner) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-2 text-center">
          <h1 className="text-xl font-semibold">This project isn&apos;t set up yet</h1>
          <p className="text-sm text-muted-foreground">
            Ask a project owner to add agents and datasets before you can start a session.
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="flex flex-1 flex-col items-center justify-center overflow-y-auto p-8">
        <div className="flex w-full max-w-lg flex-col gap-8">
          <div>
            <h1 className="text-xl font-semibold">Set up your project</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Add agents and datasets to start working with your data.
            </p>
          </div>

          <div className="flex flex-col gap-3">
            <button
              type="button"
              onClick={() => setAgentsDialogOpen(true)}
              className={cn(
                "flex cursor-pointer items-center gap-4 rounded-lg border px-5 py-4 text-left",
                "transition-colors hover:bg-accent/50",
                hasAgents
                  ? "border-green-500/40 bg-green-50/50 dark:bg-green-950/20"
                  : "border-border"
              )}
            >
              <div
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-full",
                  hasAgents ? "bg-green-100 dark:bg-green-900/40" : "bg-muted"
                )}
              >
                <Bot
                  className={cn(
                    "size-5",
                    hasAgents ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
                  )}
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Add agents</p>
                <p className="text-xs text-muted-foreground">
                  Choose one or more AI agents to use in this project
                </p>
              </div>
              {hasAgents ? (
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="size-4" />
                  <span className="text-xs font-medium">
                    {attachedAgents!.length} {attachedAgents!.length === 1 ? "agent" : "agents"}
                  </span>
                </div>
              ) : (
                <span className="text-xs font-medium text-primary">Add →</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setDatasetsDialogOpen(true)}
              className={cn(
                "flex cursor-pointer items-center gap-4 rounded-lg border px-5 py-4 text-left",
                "transition-colors hover:bg-accent/50",
                hasDatasets
                  ? "border-green-500/40 bg-green-50/50 dark:bg-green-950/20"
                  : "border-border"
              )}
            >
              <div
                className={cn(
                  "flex size-10 shrink-0 items-center justify-center rounded-full",
                  hasDatasets ? "bg-green-100 dark:bg-green-900/40" : "bg-muted"
                )}
              >
                <Folder
                  className={cn(
                    "size-5",
                    hasDatasets ? "text-green-600 dark:text-green-400" : "text-muted-foreground"
                  )}
                />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-foreground">Add datasets</p>
                <p className="text-xs text-muted-foreground">
                  Connect one or more datasets for your agents to work with
                </p>
              </div>
              {hasDatasets ? (
                <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400">
                  <CheckCircle2 className="size-4" />
                  <span className="text-xs font-medium">
                    {attachedDatasets!.length}{" "}
                    {attachedDatasets!.length === 1 ? "dataset" : "datasets"}
                  </span>
                </div>
              ) : (
                <span className="text-xs font-medium text-primary">Add →</span>
              )}
            </button>
          </div>

          <Button className="self-end" disabled={!hasAgents || !hasDatasets} onClick={onContinue}>
            Continue
          </Button>
        </div>
      </div>

      <ProjectAgentsDialog
        projectId={projectId}
        open={agentsDialogOpen}
        onOpenChange={setAgentsDialogOpen}
        attached={attachedAgents ?? []}
      />
      <ProjectDatasetsDialog
        projectId={projectId}
        open={datasetsDialogOpen}
        onOpenChange={setDatasetsDialogOpen}
        attachedIds={attachedDatasets?.map((d) => d.id) ?? []}
      />
    </>
  );
};
